import { QueryLazyOptions } from '@apollo/react-hooks'
import {
  useBuscaCidadaoListagemDesenvolvimentoInfantilLazyQuery,
  useBuscaCidadaoListagemDiabetesLazyQuery,
  useBuscaCidadaoListagemGeralLazyQuery,
  useBuscaCidadaoListagemGestantePuerperaLazyQuery,
  useBuscaCidadaoListagemHipertensaoLazyQuery,
  useBuscaCidadaoListagemPessoaIdosaLazyQuery,
  useBuscaCidadaoListagemSaudeBucalLazyQuery,
  useBuscaCidadaoListagemTematicaSaudeMulherLazyQuery,
} from 'graphql/hooks.generated'
import {
  AcompanhamentoCondicaoSaudeTematicaEnum,
  BuscaCidadaoListagemDesenvolvimentoInfantilQueryVariables,
  BuscaCidadaoListagemDiabetesQueryVariables,
  BuscaCidadaoListagemGeralQueryVariables,
  BuscaCidadaoListagemGestantePuerperaQueryVariables,
  BuscaCidadaoListagemHipertensaoQueryVariables,
  BuscaCidadaoListagemPessoaIdosaQueryVariables,
  BuscaCidadaoListagemSaudeBucalQueryVariables,
  BuscaCidadaoListagemTematicaSaudeMulherQueryVariables,
} from 'graphql/types.generated'

import { BuscaCidadaoQueryResult } from '../model/lista-tematica/model-acompanhamentoCondicaoSaudeListaTematica'

type ListaTematicaResultQueryRecord = Record<AcompanhamentoCondicaoSaudeTematicaEnum, UseBuscaCidadaoListagemResult>

type BuscaCidadaoQueryVariables = QueryLazyOptions<
  | BuscaCidadaoListagemGestantePuerperaQueryVariables
  | BuscaCidadaoListagemGeralQueryVariables
  | BuscaCidadaoListagemHipertensaoQueryVariables
  | BuscaCidadaoListagemDiabetesQueryVariables
  | BuscaCidadaoListagemPessoaIdosaQueryVariables
  | BuscaCidadaoListagemDesenvolvimentoInfantilQueryVariables
  | BuscaCidadaoListagemSaudeBucalQueryVariables
  | BuscaCidadaoListagemTematicaSaudeMulherQueryVariables
>

interface UseBuscaCidadaoListagemResult {
  cidadaos: BuscaCidadaoQueryResult
  executeQuery: (filtro: BuscaCidadaoQueryVariables) => void
  loading: boolean
  cidsConsiderados: ReadonlyArray<string>
  ciapsConsiderados: ReadonlyArray<string>
}

export function useBuscaCidadaoListagem(
  listaTematica: AcompanhamentoCondicaoSaudeTematicaEnum
): UseBuscaCidadaoListagemResult {
  const [executeQueryGeral, { data: dataGeral, loading: loadingGeral }] = useBuscaCidadaoListagemGeralLazyQuery()

  const [
    executeQueryGestantePuerpera,
    { data: dataGestantePuerpera, loading: loadingGestantePuerpera },
  ] = useBuscaCidadaoListagemGestantePuerperaLazyQuery()

  const [
    executeQueryDiabetes,
    { data: dataDiabetes, loading: loadingDiabetes },
  ] = useBuscaCidadaoListagemDiabetesLazyQuery()

  const [
    executeQueryHipertensao,
    { data: dataHipertensao, loading: loadingHipertensao },
  ] = useBuscaCidadaoListagemHipertensaoLazyQuery()

  const [
    executeQueryPessoaIdosa,
    { data: dataPessoaIdosa, loading: loadingPessoaIdosa },
  ] = useBuscaCidadaoListagemPessoaIdosaLazyQuery()

  const [
    executeQueryDesenvolvimentoInfantil,
    { data: dataDesenvolvimentoInfantil, loading: loadingDesenvolvimentoInfantil },
  ] = useBuscaCidadaoListagemDesenvolvimentoInfantilLazyQuery()

  const [
    executeSaudeMulherQuery,
    { data: dataSaudeMulher, loading: loadingSaudeMulherQuery },
  ] = useBuscaCidadaoListagemTematicaSaudeMulherLazyQuery()

  const [
    executeSaudeBucalQuery,
    { data: dataSaudeBucal, loading: loadingSaudeBucalQuery },
  ] = useBuscaCidadaoListagemSaudeBucalLazyQuery()

  const listaTematicaRecord: ListaTematicaResultQueryRecord = {
    [AcompanhamentoCondicaoSaudeTematicaEnum.GESTANTE_PUERPERA]: {
      cidadaos: dataGestantePuerpera?.acompanhamentoCondicaoSaude,
      executeQuery: executeQueryGestantePuerpera,
      loading: loadingGestantePuerpera,
      cidsConsiderados: [],
      ciapsConsiderados: [],
    },
    [AcompanhamentoCondicaoSaudeTematicaEnum.GERAL]: {
      cidadaos: dataGeral?.acompanhamentoCondicaoSaude,
      executeQuery: executeQueryGeral,
      loading: loadingGeral,
      cidsConsiderados: [],
      ciapsConsiderados: [],
    },
    [AcompanhamentoCondicaoSaudeTematicaEnum.DESENVOLVIMENTO_INFANTIL]: {
      cidadaos: dataDesenvolvimentoInfantil?.acompanhamentoCondicaoSaude,
      executeQuery: executeQueryDesenvolvimentoInfantil,
      loading: loadingDesenvolvimentoInfantil,
      cidsConsiderados: [],
      ciapsConsiderados: [],
    },
    [AcompanhamentoCondicaoSaudeTematicaEnum.DIABETES]: {
      cidadaos: dataDiabetes?.acompanhamentoCondicaoSaude,
      cidsConsiderados: dataDiabetes?.acompanhamentoCondicaoSaude.cidsConsiderados ?? [],
      ciapsConsiderados: dataDiabetes?.acompanhamentoCondicaoSaude.ciapsConsiderados ?? [],
      executeQuery: executeQueryDiabetes,
      loading: loadingDiabetes,
    },
    [AcompanhamentoCondicaoSaudeTematicaEnum.HIPERTENSAO_ARTERIAL]: {
      cidadaos: dataHipertensao?.acompanhamentoCondicaoSaude,
      cidsConsiderados: dataHipertensao?.acompanhamentoCondicaoSaude.cidsConsiderados ?? [],
      ciapsConsiderados: dataHipertensao?.acompanhamentoCondicaoSaude.ciapsConsiderados ?? [],
      executeQuery: executeQueryHipertensao,
      loading: loadingHipertensao,
    },
    [AcompanhamentoCondicaoSaudeTematicaEnum.PESSOA_IDOSA]: {
      cidadaos: dataPessoaIdosa?.acompanhamentoCondicaoSaude,
      executeQuery: executeQueryPessoaIdosa,
      loading: loadingPessoaIdosa,
      cidsConsiderados: [],
      ciapsConsiderados: [],
    },
    [AcompanhamentoCondicaoSaudeTematicaEnum.SAUDE_MULHER]: {
      cidadaos: dataSaudeMulher?.acompanhamentoCondicaoSaude,
      executeQuery: executeSaudeMulherQuery,
      loading: loadingSaudeMulherQuery,
      cidsConsiderados: [],
      ciapsConsiderados: [],
    },
    [AcompanhamentoCondicaoSaudeTematicaEnum.SAUDE_BUCAL]: {
      cidadaos: dataSaudeBucal?.acompanhamentoCondicaoSaude,
      executeQuery: executeSaudeBucalQuery,
      loading: loadingSaudeBucalQuery,
      cidsConsiderados: [],
      ciapsConsiderados: [],
    },
  }
  return (
    listaTematicaRecord[listaTematica] ?? {
      cidadaos: undefined,
      executeQuery: () => {},
      loading: false,
      cidsConsiderados: [],
      ciapsConsiderados: [],
    }
  )
}
